/**
 * LeaguesSection - Full League Experience
 *
 * Embeds the complete league detail view (standings, fixtures, registration)
 * for the league(s) associated with this Brand Hub.
 */

import { ReactElement, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import {
    FaMedal,
    FaSpinner,
    FaTrophy,
    FaUsers,
    FaCalendarAlt,
    FaInfoCircle,
    FaCheck,
    FaExclamationTriangle,
    FaChevronDown,
    FaChevronUp,
    FaEdit,
    FaTimes,
} from "react-icons/fa";
import {
    useGetLeague,
    useGetLeagueTeams,
    useGetLeagueSchedule,
    useConfirmResult,
    useDisputeResult,
    useSubmitScore,
    useGetMySquads,
    useRegisterTeam,
} from "@/competitions/leagues/hooks";
import { useGetHubCompetitions } from "@/hooks/hub/useGetHubCompetitions";
import {
    LeagueStatus,
    LeagueTeamStatus,
    LEAGUE_STATUS_LABELS,
    ROUND_ROBIN_TYPE_LABELS,
    MATCH_FORMAT_LABELS,
    Match,
    CompetitionMatchStatus,
    MATCH_STATUS_LABELS,
} from "@/competitions/leagues/types";
import "./LeaguesSection.scss";

type TabType = "standings" | "fixtures" | "teams" | "info";

interface LeaguesSectionProps {
    /** Hub slug for fetching competitions */
    surfaceSlug?: string;
    /** Competition umbrella ID (unused — data fetched via surfaceSlug) */
    competitionId?: string;
}

export const LeaguesSection = ({ surfaceSlug }: LeaguesSectionProps): ReactElement => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    // Fetch league competitions from the hub
    const { data: hubCompetitions, isLoading: isLoadingCompetitions } = useGetHubCompetitions(
        surfaceSlug,
        { type: "LEAGUE", limit: 10 },
    );

    /*
     * Get the first league ID from hub competitions
     * The HubCompetition.id IS the actual league document ID (resolved by backend)
     */
    const leagueId = hubCompetitions?.competitions?.[0]?.id;

    // State
    const [activeTab, setActiveTab] = useState<TabType>("standings");
    const [expandedMatchdays, setExpandedMatchdays] = useState<Set<number>>(new Set());
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [selectedSquadId, setSelectedSquadId] = useState<string>("");

    // Fetch league details once we have a league ID
    const { data: leagueData, isLoading: leagueLoading } = useGetLeague(leagueId);
    const { data: teamsData } = useGetLeagueTeams(leagueId);
    const { data: scheduleData } = useGetLeagueSchedule(leagueId);
    const { data: squadsData, isLoading: squadsLoading } = useGetMySquads();

    // Mutations
    const confirmMutation = useConfirmResult();
    const disputeMutation = useDisputeResult();
    const submitScoreMutation = useSubmitScore();
    const registerMutation = useRegisterTeam();

    const league = leagueData?.league;
    const teams = teamsData?.teams || [];
    const registeredTeams = teams.filter(t => t.status === LeagueTeamStatus.REGISTERED);
    const schedule = scheduleData?.matchdays || [];
    const squads = squadsData?.squads || [];

    // Find user's team in this league
    const userTeam = useMemo(() => {
        if (!fullToken?.id) return null;
        return teams.find(t => t.captainOdId === fullToken.id || t.roster?.some(r => r.odId === fullToken.id));
    }, [teams, fullToken?.id]);

    // Toggle matchday expansion
    const toggleMatchday = (matchdayNumber: number) => {
        setExpandedMatchdays(prev => {
            const next = new Set(prev);
            if (next.has(matchdayNumber)) {
                next.delete(matchdayNumber);
            } else {
                next.add(matchdayNumber);
            }
            return next;
        });
    };

    // Check if user is participant in a match
    const isUserParticipant = (match: Match): boolean => {
        if (!userTeam) return false;
        return match.participant1?.participantId === userTeam._id ||
               match.participant2?.participantId === userTeam._id;
    };

    // Check if user can confirm a match result
    const canConfirmResult = (match: Match): boolean => {
        if (!userTeam) return false;
        if (match.status !== CompetitionMatchStatus.AWAITING_CONFIRMATION) return false;
        const lastSubmission = match.submissions?.[match.submissions.length - 1];
        if (!lastSubmission) return false;
        return lastSubmission.participantId !== userTeam._id;
    };

    // Check if user can dispute a match result
    const canDisputeResult = (match: Match): boolean => {
        if (!userTeam) return false;
        if (match.status !== CompetitionMatchStatus.AWAITING_CONFIRMATION) return false;
        if (match.dispute?.active) return false;
        const lastSubmission = match.submissions?.[match.submissions.length - 1];
        if (!lastSubmission) return false;
        return lastSubmission.participantId !== userTeam._id;
    };

    // Check if user can submit score for a match
    const canSubmitScore = (match: Match): boolean => {
        if (!userTeam) return false;
        if (match.status !== CompetitionMatchStatus.SCHEDULED &&
            match.status !== CompetitionMatchStatus.AWAITING_CONFIRMATION) {
            return false;
        }
        return match.participant1?.participantId === userTeam._id ||
               match.participant2?.participantId === userTeam._id;
    };

    // Handle confirm result
    const handleConfirmResult = async (matchId: string) => {
        if (!leagueId) return;
        try {
            await confirmMutation.mutateAsync({ leagueId, matchId });
        } catch {
            // Error handled by mutation
        }
    };

    // Handle dispute result
    const handleDisputeResult = async (matchId: string) => {
        if (!leagueId) return;
        const reason = window.prompt("Please provide a reason for the dispute (minimum 10 characters):");
        if (!reason || reason.trim().length < 10) {
            alert("Dispute reason must be at least 10 characters.");
            return;
        }
        try {
            await disputeMutation.mutateAsync({
                leagueId,
                matchId,
                body: { reason: reason.trim() },
            });
        } catch {
            // Error handled by mutation
        }
    };

    // Handle score submission
    const handleSubmitScore = async (matchId: string, p1Score: number, p2Score: number) => {
        if (!leagueId) return;
        try {
            await submitScoreMutation.mutateAsync({
                leagueId,
                matchId,
                body: {
                    homeScore: p1Score,
                    awayScore: p2Score,
                },
            });
        } catch {
            // Error handled by mutation
        }
    };

    // Handle team registration
    const handleRegister = async () => {
        if (!leagueId || !selectedSquadId) return;
        try {
            await registerMutation.mutateAsync({
                leagueId,
                body: { teamId: selectedSquadId },
            });
            setShowRegisterModal(false);
            setSelectedSquadId("");
        } catch {
            // Error handled by mutation
        }
    };

    // Helper for goal difference class
    const getGDClass = (gd: number) => {
        if (gd > 0) return "positive";
        if (gd < 0) return "negative";
        return "";
    };

    // Helper for matchday status text
    const getMatchdayStatusText = (matchday: { isComplete: boolean; matchdayNumber: number }) => {
        if (matchday.isComplete) return "Complete";
        if (matchday.matchdayNumber === league?.currentMatchday) return "In Progress";
        return "Upcoming";
    };

    const formatDate = (timestamp: number | undefined) => {
        if (!timestamp) return "TBD";
        return new Date(timestamp).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const getStatusClass = (status: LeagueStatus) => {
        switch (status) {
            case LeagueStatus.REGISTRATION:
                return "registration";
            case LeagueStatus.ACTIVE:
            case LeagueStatus.PLAYOFFS:
                return "active";
            case LeagueStatus.COMPLETED:
                return "completed";
            default:
                return "default";
        }
    };

    // Loading state - waiting for hub competitions or league details
    const isLoading = isLoadingCompetitions || leagueId && leagueLoading;

    // No league found for this hub
    if (!isLoadingCompetitions && !leagueId) {
        return (
            <section className="section leagues-section">
                <div className="section__empty">
                    <FaMedal size={48} />
                    <h3>Leagues Coming Soon</h3>
                    <p>Join seasonal leagues and climb the rankings.</p>
                </div>
            </section>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <section className="section leagues-section">
                <div className="section__loading">
                    <FaSpinner className="spin" size={32} />
                    <span>Loading league...</span>
                </div>
            </section>
        );
    }

    // League data not found
    if (!league) {
        return (
            <section className="section leagues-section">
                <div className="section__empty">
                    <FaMedal size={48} />
                    <h3>Leagues Coming Soon</h3>
                    <p>Join seasonal leagues and climb the rankings.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="section leagues-section">
            {/* Header */}
            <div className="leagues-section__header">
                <div className="leagues-section__title-section">
                    <h1>{league.name}</h1>
                    {league.seasonName &&
                        <span className="leagues-section__season">{league.seasonName}</span>
                    }
                    <span className={`leagues-section__status leagues-section__status--${getStatusClass(league.status)}`}>
                        {LEAGUE_STATUS_LABELS[league.status]}
                    </span>
                </div>

                {league.status === LeagueStatus.REGISTRATION && !userTeam &&
                    <button
                        className="leagues-section__join-btn"
                        onClick={() => {
                            if (!fullToken) {
                                alert("Please log in to join this league.");
                                return;
                            }
                            setShowRegisterModal(true);
                        }}
                    >
                        Join League
                    </button>
                }
                {userTeam &&
                    <span className="leagues-section__registered-badge">
                        <FaCheck /> Registered
                    </span>
                }
            </div>

            {/* Quick Stats */}
            <div className="leagues-section__stats">
                <div className="leagues-section__stat">
                    <FaUsers />
                    <div>
                        <span className="leagues-section__stat-value">
                            {league.teamsCount}
                            {league.maxTeams ? ` / ${league.maxTeams}` : ""}
                        </span>
                        <span className="leagues-section__stat-label">Teams</span>
                    </div>
                </div>
                <div className="leagues-section__stat">
                    <FaTrophy />
                    <div>
                        <span className="leagues-section__stat-value">
                            {ROUND_ROBIN_TYPE_LABELS[league.roundRobinType]}
                        </span>
                        <span className="leagues-section__stat-label">Format</span>
                    </div>
                </div>
                <div className="leagues-section__stat">
                    <FaCalendarAlt />
                    <div>
                        <span className="leagues-section__stat-value">
                            {league.currentMatchday || 0} / {league.matchdays?.length || 0}
                        </span>
                        <span className="leagues-section__stat-label">Matchdays</span>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="leagues-section__tabs">
                <button
                    className={activeTab === "standings" ? "active" : ""}
                    onClick={() => setActiveTab("standings")}
                >
                    <FaTrophy /> Standings
                </button>
                <button
                    className={activeTab === "fixtures" ? "active" : ""}
                    onClick={() => setActiveTab("fixtures")}
                >
                    <FaCalendarAlt /> Fixtures
                </button>
                <button
                    className={activeTab === "teams" ? "active" : ""}
                    onClick={() => setActiveTab("teams")}
                >
                    <FaUsers /> Teams
                </button>
                <button
                    className={activeTab === "info" ? "active" : ""}
                    onClick={() => setActiveTab("info")}
                >
                    <FaInfoCircle /> Info
                </button>
            </div>

            {/* Tab Content */}
            <div className="leagues-section__tab-content">
                {activeTab === "standings" &&
                    <div className="leagues-section__standings">
                        {!league.standings || league.standings.length === 0
                            ? <div className="leagues-section__empty-tab">
                                <FaTrophy size={32} />
                                <p>Standings will be available once the league is active.</p>
                            </div>
                            : <div className="leagues-section__standings-table-wrapper">
                                <table className="leagues-section__standings-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Team</th>
                                            <th>P</th>
                                            <th>W</th>
                                            <th>D</th>
                                            <th>L</th>
                                            <th>GF</th>
                                            <th>GA</th>
                                            <th>GD</th>
                                            <th>Pts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {league.standings.map(standing =>
                                            <tr key={standing.teamId}>
                                                <td className="leagues-section__position">
                                                    {standing.position}
                                                </td>
                                                <td className="leagues-section__team-name">
                                                    {standing.teamName}
                                                </td>
                                                <td>{standing.played}</td>
                                                <td>{standing.wins}</td>
                                                <td>{standing.draws}</td>
                                                <td>{standing.losses}</td>
                                                <td>{standing.goalsFor}</td>
                                                <td>{standing.goalsAgainst}</td>
                                                <td className={getGDClass(standing.goalDifference)}>
                                                    {standing.goalDifference > 0 ? "+" : ""}{standing.goalDifference}
                                                </td>
                                                <td className="leagues-section__points">
                                                    {standing.points}
                                                </td>
                                            </tr>)}
                                    </tbody>
                                </table>
                            </div>
                        }
                    </div>
                }

                {activeTab === "fixtures" &&
                    <div className="leagues-section__fixtures">
                        {schedule.length === 0
                            ? <div className="leagues-section__empty-tab">
                                <FaCalendarAlt size={32} />
                                <p>Fixtures will be available once the league is active.</p>
                            </div>
                            : <div className="leagues-section__matchdays">
                                {schedule.map(matchday =>
                                    <div
                                        key={matchday.matchdayNumber}
                                        className={`leagues-section__matchday ${
                                            matchday.matchdayNumber === league.currentMatchday ? "current" : ""
                                        } ${matchday.isComplete ? "complete" : ""}`}
                                    >
                                        <button
                                            className="leagues-section__matchday-header"
                                            onClick={() => toggleMatchday(matchday.matchdayNumber)}
                                        >
                                            <div className="leagues-section__matchday-title">
                                                <h4>{matchday.name}</h4>
                                                <span className="leagues-section__matchday-status">
                                                    {getMatchdayStatusText(matchday)}
                                                </span>
                                            </div>
                                            <div className="leagues-section__matchday-meta">
                                                <span>{matchday.fixtures?.length || 0} matches</span>
                                                {matchday.scheduledDate && <span>{formatDate(matchday.scheduledDate)}</span>}
                                                {expandedMatchdays.has(matchday.matchdayNumber)
                                                    ? <FaChevronUp />
                                                    : <FaChevronDown />
                                                }
                                            </div>
                                        </button>

                                        {expandedMatchdays.has(matchday.matchdayNumber) && matchday.fixtures &&
                                            <div className="leagues-section__fixtures-list">
                                                {matchday.fixtures.map(match =>
                                                    <MatchCard
                                                        key={match.id}
                                                        match={match}
                                                        isUserMatch={isUserParticipant(match)}
                                                        canSubmit={canSubmitScore(match)}
                                                        canConfirm={canConfirmResult(match)}
                                                        canDispute={canDisputeResult(match)}
                                                        onSubmitScore={(p1, p2) => handleSubmitScore(match.id, p1, p2)}
                                                        onConfirm={() => handleConfirmResult(match.id)}
                                                        onDispute={() => handleDisputeResult(match.id)}
                                                        submitting={submitScoreMutation.isPending}
                                                        confirming={confirmMutation.isPending}
                                                        disputing={disputeMutation.isPending}
                                                    />)}
                                            </div>
                                        }
                                    </div>)}
                            </div>
                        }
                    </div>
                }

                {activeTab === "teams" &&
                    <div className="leagues-section__teams">
                        {registeredTeams.length === 0
                            ? <div className="leagues-section__empty-tab">
                                <FaUsers size={32} />
                                <p>No teams registered yet.</p>
                            </div>
                            : <div className="leagues-section__teams-grid">
                                {registeredTeams.map(team =>
                                    <div key={team._id} className="leagues-section__team-card">
                                        <div className="leagues-section__team-info">
                                            <h4>{team.name}</h4>
                                            {team.tag && <span className="leagues-section__team-tag">[{team.tag}]</span>}
                                        </div>
                                        <div className="leagues-section__team-meta">
                                            <span>{team.roster?.length || 0} players</span>
                                        </div>
                                    </div>)}
                            </div>
                        }
                    </div>
                }

                {activeTab === "info" &&
                    <div className="leagues-section__info">
                        {league.description &&
                            <div className="leagues-section__info-section">
                                <h3>About</h3>
                                <p>{league.description}</p>
                            </div>
                        }

                        <div className="leagues-section__info-grid">
                            <div className="leagues-section__info-card">
                                <h4>League Settings</h4>
                                <dl>
                                    <dt>Format</dt>
                                    <dd>{ROUND_ROBIN_TYPE_LABELS[league.roundRobinType]}</dd>
                                    <dt>Match Format</dt>
                                    <dd>{MATCH_FORMAT_LABELS[league.matchFormat]}</dd>
                                    <dt>Team Size</dt>
                                    <dd>{league.teamSize} players</dd>
                                    <dt>Min Teams</dt>
                                    <dd>{league.minTeams}</dd>
                                    {league.maxTeams &&
                                        <>
                                            <dt>Max Teams</dt>
                                            <dd>{league.maxTeams}</dd>
                                        </>
                                    }
                                </dl>
                            </div>

                            <div className="leagues-section__info-card">
                                <h4>Points System</h4>
                                <dl>
                                    <dt>Win</dt>
                                    <dd>{league.pointsConfig?.pointsPerWin ?? 3} points</dd>
                                    <dt>Draw</dt>
                                    <dd>{league.pointsConfig?.pointsPerDraw ?? 1} point</dd>
                                    <dt>Loss</dt>
                                    <dd>{league.pointsConfig?.pointsPerLoss ?? 0} points</dd>
                                </dl>
                            </div>

                            <div className="leagues-section__info-card">
                                <h4>Schedule</h4>
                                <dl>
                                    {league.registrationDeadline &&
                                        <>
                                            <dt>Registration Deadline</dt>
                                            <dd>{formatDate(league.registrationDeadline)}</dd>
                                        </>
                                    }
                                    {league.seasonStartDate &&
                                        <>
                                            <dt>Season Start</dt>
                                            <dd>{formatDate(league.seasonStartDate)}</dd>
                                        </>
                                    }
                                    {league.seasonEndDate &&
                                        <>
                                            <dt>Season End</dt>
                                            <dd>{formatDate(league.seasonEndDate)}</dd>
                                        </>
                                    }
                                </dl>
                            </div>
                        </div>
                    </div>
                }
            </div>

            {/* Registration Modal */}
            {showRegisterModal &&
                <div className="register-modal-overlay" onClick={() => setShowRegisterModal(false)} role="presentation">
                    <div className="register-modal" onClick={e => e.stopPropagation()} role="dialog">
                        <div className="register-modal__header">
                            <h3>Join {league.name}</h3>
                            <button onClick={() => setShowRegisterModal(false)}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="register-modal__content">
                            {squadsLoading &&
                                <p className="register-modal__loading">Loading your squads...</p>
                            }

                            {!squadsLoading && squads.length === 0 &&
                                <div className="register-modal__empty">
                                    <FaUsers size={32} />
                                    <p>You don't have any squads yet.</p>
                                    <p>Create a squad first to join this league.</p>
                                </div>
                            }

                            {!squadsLoading && squads.length > 0 &&
                                <>
                                    <p className="register-modal__instructions">
                                        Select a squad to register for this league:
                                    </p>
                                    <div className="register-modal__squads">
                                        {squads.map(squad =>
                                            <button
                                                key={squad._id}
                                                className={`register-modal__squad ${selectedSquadId === squad._id ? "selected" : ""}`}
                                                onClick={() => setSelectedSquadId(squad._id)}
                                            >
                                                <span className="register-modal__squad-name">{squad.name}</span>
                                                {squad.tag && <span className="register-modal__squad-tag">[{squad.tag}]</span>}
                                                <span className="register-modal__squad-members">
                                                    {squad.members?.length || 0} members
                                                </span>
                                            </button>)}
                                    </div>
                                </>
                            }

                            {registerMutation.isError &&
                                <p className="register-modal__error">
                                    {registerMutation.error?.message || "Failed to register. Please try again."}
                                </p>
                            }
                        </div>

                        <div className="register-modal__footer">
                            <button
                                className="register-modal__btn register-modal__btn--cancel"
                                onClick={() => setShowRegisterModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="register-modal__btn register-modal__btn--submit"
                                onClick={handleRegister}
                                disabled={!selectedSquadId || registerMutation.isPending}
                            >
                                {registerMutation.isPending ? "Registering..." : "Register Squad"}
                            </button>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
};

// Match Card Component
interface MatchCardProps {
    match: Match;
    isUserMatch: boolean;
    canSubmit: boolean;
    canConfirm: boolean;
    canDispute: boolean;
    onSubmitScore: (p1Score: number, p2Score: number) => void;
    onConfirm: () => void;
    onDispute: () => void;
    submitting: boolean;
    confirming: boolean;
    disputing: boolean;
}

function MatchCard({
    match,
    isUserMatch,
    canSubmit,
    canConfirm,
    canDispute,
    onSubmitScore,
    onConfirm,
    onDispute,
    submitting,
    confirming,
    disputing,
}: MatchCardProps): ReactElement {
    const [showScoreForm, setShowScoreForm] = useState(false);
    const [p1Score, setP1Score] = useState<string>("");
    const [p2Score, setP2Score] = useState<string>("");

    const getStatusClass = (status: CompetitionMatchStatus) => {
        switch (status) {
            case CompetitionMatchStatus.COMPLETED:
                return "completed";
            case CompetitionMatchStatus.AWAITING_CONFIRMATION:
                return "awaiting";
            case CompetitionMatchStatus.DISPUTED:
                return "disputed";
            case CompetitionMatchStatus.SCHEDULED:
            default:
                return "scheduled";
        }
    };

    const formatMatchTime = (timestamp?: number) => {
        if (!timestamp) return null;
        return new Date(timestamp).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    const handleSubmit = () => {
        const score1 = parseInt(p1Score, 10);
        const score2 = parseInt(p2Score, 10);
        if (isNaN(score1) || isNaN(score2) || score1 < 0 || score2 < 0) {
            alert("Please enter valid scores (0 or higher).");
            return;
        }
        onSubmitScore(score1, score2);
        setShowScoreForm(false);
        setP1Score("");
        setP2Score("");
    };

    const p1 = match.participant1;
    const p2 = match.participant2;
    const hasScores = p1?.score !== undefined && p2?.score !== undefined;

    return (
        <div className={`match-card ${isUserMatch ? "match-card--user" : ""} match-card--${getStatusClass(match.status)}`}>
            <div className="match-card__teams">
                <div className={`match-card__team ${match.winnerId === p1?.participantId ? "winner" : ""}`}>
                    <span className="match-card__team-name">{p1?.displayName || "TBD"}</span>
                    {hasScores && <span className="match-card__score">{p1?.score}</span>}
                </div>
                <span className="match-card__vs">vs</span>
                <div className={`match-card__team ${match.winnerId === p2?.participantId ? "winner" : ""}`}>
                    {hasScores && <span className="match-card__score">{p2?.score}</span>}
                    <span className="match-card__team-name">{p2?.displayName || "TBD"}</span>
                </div>
            </div>

            <div className="match-card__info">
                <span className={`match-card__status match-card__status--${getStatusClass(match.status)}`}>
                    {MATCH_STATUS_LABELS[match.status]}
                </span>
                {match.scheduledStartTime &&
                    <span className="match-card__time">{formatMatchTime(match.scheduledStartTime)}</span>
                }
                {match.isDraw && <span className="match-card__draw">Draw</span>}
            </div>

            {/* Score Submission Form */}
            {showScoreForm &&
                <div className="match-card__score-form">
                    <div className="match-card__score-inputs">
                        <div className="match-card__score-input-group">
                            <label>{p1?.displayName || "Team 1"}</label>
                            <input
                                type="number"
                                min="0"
                                value={p1Score}
                                onChange={e => setP1Score(e.target.value)}
                                placeholder="0"
                            />
                        </div>
                        <span className="match-card__score-separator">-</span>
                        <div className="match-card__score-input-group">
                            <label>{p2?.displayName || "Team 2"}</label>
                            <input
                                type="number"
                                min="0"
                                value={p2Score}
                                onChange={e => setP2Score(e.target.value)}
                                placeholder="0"
                            />
                        </div>
                    </div>
                    <div className="match-card__score-actions">
                        <button
                            className="match-card__btn match-card__btn--submit"
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting ? "Submitting..." : "Submit Score"}
                        </button>
                        <button
                            className="match-card__btn match-card__btn--cancel"
                            onClick={() => setShowScoreForm(false)}
                            disabled={submitting}
                        >
                            <FaTimes /> Cancel
                        </button>
                    </div>
                </div>
            }

            {/* Action Buttons */}
            {isUserMatch && !showScoreForm && (canSubmit || canConfirm || canDispute) &&
                <div className="match-card__actions">
                    {canSubmit &&
                        <button
                            className="match-card__btn match-card__btn--primary"
                            onClick={() => setShowScoreForm(true)}
                        >
                            <FaEdit /> Submit Score
                        </button>
                    }
                    {canConfirm &&
                        <button
                            className="match-card__btn match-card__btn--confirm"
                            onClick={onConfirm}
                            disabled={confirming}
                        >
                            <FaCheck /> {confirming ? "Confirming..." : "Confirm Result"}
                        </button>
                    }
                    {canDispute &&
                        <button
                            className="match-card__btn match-card__btn--dispute"
                            onClick={onDispute}
                            disabled={disputing}
                        >
                            <FaExclamationTriangle /> {disputing ? "Disputing..." : "Dispute"}
                        </button>
                    }
                </div>
            }

            {match.dispute?.active &&
                <div className="match-card__dispute-notice">
                    <FaExclamationTriangle />
                    <span>This match is under dispute. An admin will review.</span>
                </div>
            }
        </div>
    );
}
